/* eslint-disable lines-around-comment */
import { SerializedError, createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { LoadingState, Redux } from '../..'

export type NotificationCategory = 'restaurant' | 'menu'

/** Matches websocket / server payloads for special UI (sound, bell animation). */
export type NotificationEventKind =
  | 'ingredient_auto_reenable'
  | 'ingredient_pre_expiry_reminder'
  | 'equipment_permanent_disable_decision'
  | 'equipment_auto_reenable'

type StateSlice<T> = {
  value: T
  loadingState: LoadingState
  error: SerializedError | null
}

function newClientKey(): string {
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

export type Notification = {
  // Server row id when returned from GET /resto365-notifications/my
  id?: string
  /** Stable id for mark-as-read in the current session (required for socket notifications). */
  clientKey?: string
  userId?: number
  message: string
  category: NotificationCategory
  createdAt?: Date
  read?: boolean
  eventKind?: NotificationEventKind
}

type InitialStateType<K> = K extends StateSlice<infer T> ? T : never

function getInitialStateFor<K>(initialState: InitialStateType<K>) {
  return {
    value: initialState,
    loadingState: 'initial' as LoadingState,
    error: null
  } as StateSlice<InitialStateType<K>>
}

export const fetchUserNotifications = createAsyncThunk<Notification[], void, Redux>(
  'api/fetchUserNotifications',
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient(`resto365-notifications/my`)
      const {
        data: { data: notifications }
      } = response

      return notifications
    } catch {
      throw new Error('Failed to fetch notifications')
    }
  }
)

function normalizeCreatedAt(n: Notification): Notification {
  const ca = n.createdAt
  const createdAt = ca instanceof Date ? ca : ca != null && ca !== '' ? new Date(ca as unknown as string) : undefined

  return { ...n, ...(createdAt != null ? { createdAt } : {}) }
}

type StoreWithNotifications = {
  resto365Notifications: {
    notifications: { value: Notification[] }
  }
}

export const markNotificationReadOnServer = createAsyncThunk<string, string, Redux>(
  'api/markNotificationReadOnServer',
  async (clientKey, { getState, extra }) => {
    const list = (getState() as StoreWithNotifications).resto365Notifications.notifications.value
    const n = list.find(x => x.clientKey === clientKey)
    if (n?.id) {
      await extra.httpClient.patch(`resto365-notifications/${n.id}/read`)
    }

    return clientKey
  }
)

export const markAllNotificationsReadOnServer = createAsyncThunk<void, void, Redux>(
  'api/markAllNotificationsReadOnServer',
  async (_, { extra }) => {
    await extra.httpClient.post(`resto365-notifications/my/read-all`)
  }
)

export const deleteAllNotificationsOnServer = createAsyncThunk<void, void, Redux>(
  'api/deleteAllNotificationsOnServer',
  async (_, { extra }) => {
    await extra.httpClient.delete(`resto365-notifications/my`)
  }
)

const resto365Notifications = createSlice({
  name: 'resto365Notifications',
  initialState: {
    notifications: getInitialStateFor<StateSlice<Notification[]>>([])
  },
  reducers: {
    clearNotifications: state => {
      state.notifications.value = []
      state.notifications.loadingState = 'initial'
      state.notifications.error = null
    },
    markAllRead: state => {
      state.notifications.value = state.notifications.value.map(notification => ({
        ...notification,
        read: true
      }))
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const key = action.payload
      state.notifications.value = state.notifications.value.map(n => (n.clientKey === key ? { ...n, read: true } : n))
    },
    addNotification: (state, action: { payload: Notification }) => {
      const p = action.payload
      const clientKey = p.clientKey ?? (p.id != null ? String(p.id) : newClientKey())
      const id = p.id
      if (id != null) {
        state.notifications.value = state.notifications.value.filter(n => n.id !== id)
      }
      state.notifications.value = [
        { ...normalizeCreatedAt(p), clientKey, read: p.read ?? false },
        ...state.notifications.value
      ]
    }
  },
  extraReducers: builder => {
    ;(builder.addCase(fetchUserNotifications.fulfilled, (state, action) => {
      state.notifications.loadingState = 'success'
      state.notifications.value = action.payload.map(raw => {
        const n = normalizeCreatedAt(raw as Notification)
        const row = n as Notification & { id?: string }
        const clientKey = row.clientKey ?? (row.id != null ? String(row.id) : newClientKey())

        return { ...n, clientKey, read: n.read ?? false }
      })
    }),
      builder.addCase(fetchUserNotifications.pending, state => {
        state.notifications.loadingState = 'pending'
      }),
      builder.addCase(fetchUserNotifications.rejected, (state, action) => {
        state.notifications.loadingState = 'error'
        state.notifications.error = action.error
      }),
      builder.addCase(markNotificationReadOnServer.fulfilled, (state, action) => {
        const key = action.payload
        state.notifications.value = state.notifications.value.map(n => (n.clientKey === key ? { ...n, read: true } : n))
      }),
      builder.addCase(markAllNotificationsReadOnServer.fulfilled, state => {
        state.notifications.value = state.notifications.value.map(n => ({ ...n, read: true }))
      }),
      builder.addCase(deleteAllNotificationsOnServer.fulfilled, state => {
        state.notifications.value = []
      }))
  }
})

export default resto365Notifications.reducer
export const { clearNotifications, markAllRead, markNotificationRead, addNotification } = resto365Notifications.actions
